import { CommonModule, DatePipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  signal,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { finalize } from 'rxjs';
import {
  BreadcrumbItem,
  BreadcrumbsComponent,
} from '../../../../shared/components/breadcrumbs/breadcrumbs.component';
import { UserApiModel } from '../../../users/models/user.models';
import { UsersService } from '../../../users/services/users.service';
import { AuditLogItem } from '../../models/audit-log.models';
import { AuditLogService } from '../../services/audit-log.service';

@Component({
  selector: 'app-audit-log',
  imports: [CommonModule, FormsModule, DatePipe, BreadcrumbsComponent],
  templateUrl: './audit-log.component.html',
  styleUrl: './audit-log.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AuditLogComponent {
  private readonly auditLogService = inject(AuditLogService);
  private readonly usersService = inject(UsersService);

  readonly loading = signal(false);
  readonly loadingUsers = signal(false);
  readonly errorMessage = signal<string | null>(null);

  readonly logs = signal<AuditLogItem[]>([]);
  readonly eventTypes = signal<string[]>([]);
  readonly users = signal<UserApiModel[]>([]);

  readonly selectedEventType = signal<string>('all');
  readonly selectedActorUserId = signal<number | null>(null);
  readonly selectedStartDate = signal<string>('');
  readonly selectedEndDate = signal<string>('');

  readonly breadcrumbItems: BreadcrumbItem[] = [
    { label: 'Home', route: '/home' },
    { label: 'Audit Log' },
  ];

  readonly totalLogs = computed(() => this.logs().length);

  constructor() {
    this.loadUsers();
    this.loadAuditLogs();
  }

  setEventTypeFilter(value: string): void {
    this.selectedEventType.set(value);
    this.loadAuditLogs();
  }

  applyFilters(): void {
    this.loadAuditLogs();
  }

  clearDateFilters(): void {
    this.selectedStartDate.set('');
    this.selectedEndDate.set('');
    this.loadAuditLogs();
  }

  actorDisplayName(log: AuditLogItem): string {
    if (!log.actorName) {
      return 'System';
    }

    if (!log.actorRole) {
      return log.actorName;
    }

    return `${log.actorName} (${log.actorRole})`;
  }

  metadataPreview(log: AuditLogItem): string {
    if (!log.metadata) {
      return '-';
    }

    const entries = Object.entries(log.metadata).slice(0, 4);
    if (entries.length === 0) {
      return '-';
    }

    return entries.map(([key, value]) => `${key}: ${String(value)}`).join(', ');
  }

  trackByLogId(_index: number, item: AuditLogItem): number {
    return item.id;
  }

  private loadUsers(): void {
    this.loadingUsers.set(true);

    this.usersService
      .listUsers()
      .pipe(finalize(() => this.loadingUsers.set(false)))
      .subscribe({
        next: ({ data }) => {
          this.users.set(data ?? []);
        },
        error: () => {
          this.users.set([]);
        },
      });
  }

  private loadAuditLogs(): void {
    this.loading.set(true);
    this.errorMessage.set(null);

    this.auditLogService
      .getAuditLogs({
        eventType: this.selectedEventType() === 'all' ? undefined : this.selectedEventType(),
        actorUserId: this.selectedActorUserId() ?? undefined,
        startDate: this.selectedStartDate().trim() || undefined,
        endDate: this.selectedEndDate().trim() || undefined,
      })
      .pipe(finalize(() => this.loading.set(false)))
      .subscribe({
        next: (response) => {
          this.logs.set(response.data ?? []);
          this.eventTypes.set(response.eventTypes ?? []);

          if (
            this.selectedEventType() !== 'all'
            && this.selectedEventType().length > 0
            && !(response.eventTypes ?? []).includes(this.selectedEventType())
          ) {
            this.selectedEventType.set('all');
          }
        },
        error: (error) => {
          this.errorMessage.set(error?.error?.message ?? 'Unable to load audit logs right now.');
          this.logs.set([]);
        },
      });
  }
}
